<template>
  <v-app>
    <ToastContainer :dark="isDarkTheme" />
    <Menu
      v-model="isOpenSimulator"
      v-if="shoudShowMenu()"
      :key="key"
    />
    <OperationSimulator
      v-model="isOpenSimulator"
      v-if="shoudShowSimulator()"
    />

    <!-- A Clara vive AQUI, fora do <Menu>. O Menu tem `:key` e é remontado
         sempre que os acessos mudam ou o token é renovado — com o chat lá
         dentro, a conversa era apagada no meio do uso. -->
    <ClaraPanel v-if="shoudShowMenu() && claraDisponivel" />

    <!-- Guia de apresentação da Clara. Fica FORA do `v-if` do menu de propósito:
         ele precisa sobreviver à navegação para não reabrir a cada troca de
         rota. QUANDO aparecer é decisão do próprio componente; o `v-if` aqui
         responde só por PARA QUEM — não faz sentido apresentar uma ferramenta
         que o perfil não tem. -->
    <ClaraOnboardingGuide v-if="claraDisponivel" />

    <!-- Chat de suporte (DeepDesk). Tambem fora do <Menu>, pelo mesmo motivo da
         Clara, e SEM `v-if` no menu: nas telas sem menu ele so se esconde. Quem
         pode ter o chat e decisao do proprio componente. -->
    <SupportWidgetHost :is-shell-visible="shoudShowMenu()" />

    <v-main
      class="main-content-area"
      :class="{ 'main-content-area--external': isExternalAccessRoute }"
      :style="{ paddingRight: claraOffset ? `${claraOffset}px` : undefined }"
    >
      <div :class="contentPanelClass">
        <router-view />
      </div>

      <MoneyLoader :loading="loading" />

      <div
        v-if="shouldShowFooter"
        class="footer"
      >
        <div class="by">
          &copy; {{ new Date().getFullYear() }} - Powered by DeepRocket Tecnologia LTDA
        </div>
        <div by="version">{{ version }}</div>
      </div>
    </v-main>
  </v-app>
</template>

<script>
//Constants
import { OPTIONS_STORE_ACCESS } from './vuex/module/access';
import { OPTIONS_STORE_CLARA } from './vuex/module/clara';
import { SOCKET_EVENT_NAME } from '../../common/constants/global';
import { PROFILES } from '../../common/constants/generic/types';
import { ROUTE_NAMES } from '@/constants/route-constants';
import { initializePreSaleLockSocket, requestInitialPreSaleLocks } from '@/services/preSaleLockSocket';
import SketchApi from '@/api/general/sketch-api';

// Components
import Menu from '@/components/menu/Menu';
import OperationSimulator from '@/components/comum/OperationSimulator';
import ToastContainer from '@/components/comum/ToastContainer';
import MoneyLoader from '@/components/comum/MoneyLoader.vue';
import ClaraPanel from '@/views/clara/ClaraPanel.vue';
import ClaraOnboardingGuide from '@/views/clara/ClaraOnboardingGuide.vue';
import { claraDisponivelPara } from '@/views/clara/claraAccess';
import SupportWidgetHost from '@/views/support/SupportWidgetHost.vue';

// Mixins
import mixinAuthorization from '@/mixin/mixin-authorization';
import mixinMessage from '@/mixin/mixin-message';
import mixinOnboardingChecklist from '@/mixin/mixin-onboarding-checklist';

// Onboarding
import OnboardingApi from '@/api/onboarding/onboarding-api';

// Storage
import utilsStorage from '@/utils/storage';

import { mapGetters } from 'vuex/dist/vuex.common.js';
import socketIOClient from 'socket.io-client';

import SocketData from '../../common/models/SocketData';
import { sendPushNotification, askNotificationPermission } from './utils/notification';
import storage from './utils/storage';
import { isCustomerFormExternalAccessPath, isOnCustomerFormExternalAccess } from '@/utils/external-access-route';

/** Paginas abertas por visitantes sem sessao: nao carregam acessos, socket nem onboarding. */
const PUBLIC_PAGES_WITHOUT_SESSION = [
  ROUTE_NAMES.CUSTOMER_OPERATION_APPROVAL,
  ROUTE_NAMES.CORBAN_PUBLIC_SIGNUP,
  ROUTE_NAMES.FORM_EDIT_CUSTOMER_EXTERNAL_ACCESS,
  ROUTE_NAMES.CUSTOMER_SELFIE_EXTERNAL_ACCESS
];

export default {
  name: 'App',
  mixins: [mixinAuthorization, mixinMessage, mixinOnboardingChecklist],
  components: {
    Menu,
    OperationSimulator,
    ToastContainer,
    MoneyLoader,
    ClaraPanel,
    ClaraOnboardingGuide,
    SupportWidgetHost
  },

  provide() {
    const appData = {};

    /* Necessário para que a propriedade seja reativa */
    Object.defineProperty(appData, 'currentUser', {
      get: () => this.user,
      set: (value) => {
        this.user = value;
        storage.saveUserOnStorage(value);
      }
    });

    Object.defineProperty(appData, 'pendingPreSale', {
      get: () => this.pendingPreSale,
      set: (value) => {
        this.pendingPreSale = value;
      }
    });

    Object.defineProperty(appData, 'isMobile', {
      get: () => this.$vuetify.breakpoint.smAndDown
    });

    Object.defineProperty(appData, 'notificationList', {
      get: () => this.notificationList,
      set: (value) => {
        this.notificationList = value;
      }
    });

    Object.defineProperty(appData, 'pendingCustomerWorkflow', {
      get: () => this.pendingCustomerWorkflow,
      set: (value) => {
        this.pendingCustomerWorkflow = value;
      }
    });

    Object.defineProperty(appData, 'customerAccessMode', {
      get: () => Number(this.user.profileId) === PROFILES.CUSTOMER.id && !!this.user.customerId
    });

    Object.defineProperty(appData, 'quotation', {
      get: () => this.quotation,
      set: (value) => {
        this.quotation = value;
      }
    });

    return {
      appData
    };
  },

  data() {
    return {
      /* Lembra se a Clara estava aberta antes de uma tela cheia, para devolver
         a conversa ao sair dela. */
      shouldRestoreClara: false,
      isOpenSimulator: false,
      version: `${import.meta.env.VUE_APP_NAME} - ${import.meta.env.VUE_APP_APP_VERSION}`,
      key: 0,
      loading: false,
      socket: null,
      pendingPreSale: false,
      notificationList: [],
      pendingCustomerWorkflow: false,
      quotation: {},
      sketchApi: null,
    };
  },
  watch: {

    /* Fecha a Clara ao entrar numa tela cheia e REABRE ao sair, se estava
       aberta. Fechar sem lembrar puniria quem estava no meio de uma conversa. */
    '$route.meta.hideClara': {
      immediate: true,
      handler(hide) {
        const isOpen = this.$store.getters[OPTIONS_STORE_CLARA.GETTERS.IS_OPEN];

        if (hide) {
          this.shouldRestoreClara = isOpen;

          if (isOpen) this.$store.commit(OPTIONS_STORE_CLARA.MUTATIONS.SET_OPEN, false);

          return;
        }

        if (this.shouldRestoreClara) {
          this.shouldRestoreClara = false;
          this.$store.commit(OPTIONS_STORE_CLARA.MUTATIONS.SET_OPEN, true);
        }
      }
    },
    getAccess: {
      handler() {
        this.key++;
      },
      deep: true
    },
    $route: {
      handler() {
        this._applyExternalLayout();
      },
      immediate: true
    }
  },

  async mounted() {
    const isPublicRoute = this.isPageWithoutSession();

    if (this.user?.id && !isPublicRoute && (!this.getAccess || this.getAccess.length === 0)) {
      await this.$store.dispatch(OPTIONS_STORE_ACCESS.ACTIONS.FIND_ACCESS, this.user);
      this.key++;
    }

    this.verifyTheme();
    this.$eventBus.$on('show-loading', this.showLoading);
    this.$eventBus.$on('token-changed', this._onTokenChanged);
    this.$root.$on('sendMessage', this._onSendMessage);

    if (!isPublicRoute)
      this.startSocket();

    if (!isPublicRoute && this.user?.id)
      this.loadPreSaleBadge();

    if (!isPublicRoute && this.user?.id)
      this.initOnboardingChecklist();
  },
  beforeDestroy() {
    if (this.socket) this.socket.close();

    this.$eventBus.$off('show-loading', this.showLoading);
    this.$eventBus.$off('token-changed', this._onTokenChanged);
    this.$root.$off('sendMessage', this._onSendMessage);
  },
  computed: {
    ...mapGetters({
      getAccess: OPTIONS_STORE_ACCESS.GETTERS.GET_ACCESS
    }),
    isDarkTheme() {
      return this.$vuetify?.theme?.dark ?? false;
    },
    /* A Clara é ferramenta da operação interna: ela lê carteira de clientes,
       operações e limites do tenant inteiro. Nos perfis Corban e Cliente esse
       alcance não faz sentido, então ela não é montada — não basta esconder,
       porque o painel montado assina a store e reserva o recuo do conteúdo. */
    claraDisponivel() {
      return claraDisponivelPara(this.user);
    },
    /* Recuo do conteúdo enquanto a Clara está aberta: ela empurra o app em vez
       de cobri-lo, para continuar dando pra ler a tela enquanto se conversa.
       No mobile o painel ocupa a largura toda e não há o que empurrar. */
    claraOffset() {
      if (!this.claraDisponivel) return 0;

      if (this.$vuetify?.breakpoint?.smAndDown) return 0;

      /* Telas cheias (`hideClara`) nao convivem com o painel. O recuo tem de
         zerar AQUI e nao no painel: nessas rotas o ClaraPanel nem e montado
         (shoudShowMenu as exclui), entao um watch la dentro nunca rodaria e a
         faixa vazia continuava ao lado do wizard. */
      if (this.$route?.meta?.hideClara) return 0;

      return this.$store.getters[OPTIONS_STORE_CLARA.GETTERS.CONTENT_OFFSET];
    },
    shouldShowFooter() {
      const path = this.$route.path;

      return (
        !this.user?.customerId &&
        !path.includes('customer-access') &&
        !path.includes('external-access') &&
        this.$route.name !== ROUTE_NAMES.CORBAN_PUBLIC_SIGNUP &&
        this.$route.name !== ROUTE_NAMES.CUSTOMER_OPERATION_APPROVAL
      );
    },
    isExternalAccessRoute() {
      const path = this.$route.path;

      return path.includes('external-access') || isCustomerFormExternalAccessPath(path) || [ROUTE_NAMES.CORBAN_PUBLIC_SIGNUP, ROUTE_NAMES.CUSTOMER_OPERATION_APPROVAL].includes(this.$route.name);
    },
    contentPanelClass() {
      return this.isExternalAccessRoute ? 'content-panel content-panel--full' : 'content-panel';
    }
  },
  methods: {
    isPageWithoutSession() {
      return PUBLIC_PAGES_WITHOUT_SESSION.includes(this.$router.currentRoute.name) || isOnCustomerFormExternalAccess();
    },

    async _onTokenChanged() {
      this.getUser();
      const isPublicRoute = this.isPageWithoutSession();
      
      if (this.user?.id && !isPublicRoute && (!this.getAccess || this.getAccess.length === 0)) {
        await this.$store.dispatch(OPTIONS_STORE_ACCESS.ACTIONS.FIND_ACCESS, this.user);
        this.key++;
      }

      if (this.user?.id && !isPublicRoute) this.initOnboardingChecklist();
    },

    async loadPreSaleBadge() {
      if (!this.user?.id) return;

      try {
        if (!this.sketchApi)
          this.sketchApi = new SketchApi(this.user);

        const last24h = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const response = await this.sketchApi.find({ startDate: last24h });
        const sketches = response?.data || [];

        const hasOpenOperations = sketches.some(
          (sketch) => sketch.isUserCorban || sketch.isUserCustomer
        );

        this.$eventBus.$emit('pre-sale-open-operations-loaded', { hasOpenOperations });
      } catch (error) {
        // eslint-disable-next-line no-empty
      }
    },

    startSocket() {
      if (this.user.id) {
        this.initializeSocketConnection();

        return;
      }

      const intervalId = setInterval(() => {
        if (this.user.id) {
          clearInterval(intervalId);
          this.initializeSocketConnection();

          return;
        }
      }, 250);
    },

    async initializeSocketConnection() {
      if (this.socket || this.isPageWithoutSession()) return;


      const response = await fetch('/config.json');
      const config = await response.json();
      const urlBase = config?.urlBase;

      if (!urlBase) return;

      /* O backend identifica o usuário pelo JWT (handshake.auth), não pela
         query — a query podia ser forjada para receber notificações de outro
         usuário. */
      this.socket = socketIOClient(urlBase, {
        transports: ['websocket'],
        // Função: o token é relido a cada (re)conexão. Objeto fixo reconectava com o token velho
        // e a sessão voltava anônima, sem os avisos do usuário.
        auth: (callback) => callback({ token: storage.getTokenOnStorage() })
      });

      this.$root.socket = this.socket;
      initializePreSaleLockSocket({ socket: this.socket, store: this.$store });

      /* No socket.io 4 o 'reconnect' é do gerenciador (socket.io), não do socket: o ouvinte antigo
         nunca disparava. Avisos enviados com a conexão caída (ex.: deploy) se perdem, então as
         telas abertas recarregam o que mostram. */
      this.socket.io.on('reconnect', () => {
        // eslint-disable-next-line no-console
        console.info('[Socket.IO] Reconexão detectada — re-solicitando dados de bloqueio de pré-venda');
        requestInitialPreSaleLocks();
        this.$eventBus.$emit('socket-reconnected');
      });

      this.socket.on('handshack', () => {
        // eslint-disable-next-line no-console
        console.log('Conexão com WebSocket estabelecida!');
        askNotificationPermission();
      });

      this.socket.on('disconnect', () => {
        // eslint-disable-next-line no-console
        console.log('Desconectado do servidor');
      });

      /* O backend resolve os destinatários e emite só para eles, então não há
         mais filtro por perfil aqui — quem recebe o evento é porque a
         notificação é dele. A gravação também saiu daqui: era o browser de
         quem estava online que criava o registro, e por isso o resultado
         dependia de quem tinha a tela aberta. */
      const NOTIFICATION_EVENTS = [
        { event: SOCKET_EVENT_NAME.PRE_VENDA, refreshPreSale: true },
        { event: SOCKET_EVENT_NAME.PRE_VENDA_EXTERNA, refreshPreSale: true, flagPreSale: true },
        { event: SOCKET_EVENT_NAME.PRE_VENDA_CLIENTE, refreshPreSale: true, flagPreSale: true },
        { event: SOCKET_EVENT_NAME.PRE_VENDA_DOLSPAY },
        { event: SOCKET_EVENT_NAME.FICHA_ASSINADA, flagCustomerWorkflow: true },
        { event: SOCKET_EVENT_NAME.RECEIVER_ENVIADO },
        { event: SOCKET_EVENT_NAME.RECEIVER_APROVADO },
        { event: SOCKET_EVENT_NAME.RECEIVER_PENDENTE },
        { event: SOCKET_EVENT_NAME.RECEIVER_FALHA },
        { event: SOCKET_EVENT_NAME.DUIMP_ATUALIZADO },
        { event: SOCKET_EVENT_NAME.BANK_ONBOARDING_STATUS_CHANGED },
        { event: SOCKET_EVENT_NAME.RFI_COMPLIANCE },
        { event: SOCKET_EVENT_NAME.LIMITE_AUMENTO },
        { event: SOCKET_EVENT_NAME.COMISSAO_URGENTE },
        { event: SOCKET_EVENT_NAME.CADASTRO_APROVADO_AUTOMATICO, flagCustomerWorkflow: true }
      ];

      NOTIFICATION_EVENTS.forEach((config) => {
        this.socket.on(config.event, (socketData) => {
          if (this._isDuplicateNotification(socketData)) return;

          this.notificationList.push(socketData);
          this.$eventBus.$emit('notification-updated');
          this.interpretMessage(socketData);

          if (config.flagPreSale) this.pendingPreSale = true;

          if (config.flagCustomerWorkflow) this.pendingCustomerWorkflow = true;

          if (config.refreshPreSale) this.loadPreSaleBadge();
        });
      });

      /* Pagamento e linha do tempo do DolsPay: repassados pelo barramento. A tela não depende de o
         socket já existir quando ela monta (no F5 ele nasce depois do config.json). */
      this.socket.on(SOCKET_EVENT_NAME.STABLECOIN_PAYMENT_UPDATED, (data) => this.$eventBus.$emit('stablecoin-payment-updated', data));
      this.socket.on(SOCKET_EVENT_NAME.STABLECOIN_TIMELINE_UPDATED, (data) => this.$eventBus.$emit('stablecoin-timeline-updated', data));

      this.socket.on(SOCKET_EVENT_NAME.PRE_VENDA_REMOVIDA, (data) => {
        this.$eventBus.$emit('pre-sale-removed', data);
        this.loadPreSaleBadge();
      });

      // WhatsApp: atualizar badge global quando mensagem recebida (direction != 'sent')
      this.socket.on('whatsapp:new-message', (messageData) => {
        if (messageData && messageData.direction !== 'sent') {
          this.$eventBus.$emit('whatsapp:unread-refresh');
        }
      });
    },

    _isDuplicateNotification(socketData) {
      const key = `${socketData?.message}|${socketData?.customerMessage}|${socketData?.date}`;

      return this.notificationList.some(
        (n) => `${n?.message}|${n?.customerMessage}|${n?.date}` === key
      );
    },

    /*
     * Metodo que vai saber receber a mensagem e saber delegar para quem for necessário
     */
    interpretMessage(data) {
      const socketData = new SocketData(data);

      sendPushNotification(socketData);
    },

    verifyTheme() {
      this.$vuetify.theme.dark = utilsStorage.getThemeSettings();
    },
    showLoading(loading) {
      this.loading = loading;
    },
    _onSendMessage(message, type) {
      this.sendMessage(message, type);
    },
    shoudShowMenu() {
      const FORMS_DONT_SHOW_MENU = [
        ROUTE_NAMES.LOGIN,
        ROUTE_NAMES.FORM_EDIT_CUSTOMER_EXTERNAL_ACCESS,
        ROUTE_NAMES.FORM_EDIT_CORRESPONDENT_EXTERNAL_ACCESS,
        ROUTE_NAMES.FILE_VIEW,
        ROUTE_NAMES.FORM_CUSTOMER_FINALLY,
        ROUTE_NAMES.CUSTOMER_DOCUMENTS_EXTERNAL_ACCESS,
        ROUTE_NAMES.OPERATION_DOCUMENTS_EXTERNAL_ACCESS,
        ROUTE_NAMES.CUSTOMER_SELFIE_EXTERNAL_ACCESS,
        ROUTE_NAMES.CUSTOMER_OPERATION_APPROVAL,
        ROUTE_NAMES.DUIMP_EXTERNAL_ACCESS,
        ROUTE_NAMES.BANK_DOCUMENTS_EXTERNAL_ACCESS,
        ROUTE_NAMES.FIRST_STEPS,
        ROUTE_NAMES.CORBAN_PUBLIC_SIGNUP
      ];

      return (
        !!this.$router.currentRoute.name &&
        !FORMS_DONT_SHOW_MENU.includes(this.$router.currentRoute.name)
      );
    },
    shoudShowSimulator() {
      return !!(
        this.$router.currentRoute.meta.requireAuth &&
        this.isOpenSimulator &&
        this.$router.currentRoute.name != 'FileView'
      );
    },
    _applyExternalLayout() {
      this.$nextTick(() => {
        const wrap = this.$el?.querySelector('.v-main__wrap');

        if (!wrap) return;

        if (this.isExternalAccessRoute) {
          wrap.style.paddingTop = '0';
          wrap.style.paddingLeft = '0';
          wrap.style.paddingRight = '0';
          wrap.style.overflow = '';
          // O Vuetify reserva a faixa da barra de rolagem mesmo sem rolagem: no link do cliente ela tira o card do centro
          document.documentElement.style.overflowY = this.$route.name === ROUTE_NAMES.CUSTOMER_OPERATION_APPROVAL ? 'auto' : '';

          return;
        }

        wrap.style.paddingTop = '';
        wrap.style.paddingLeft = '';
        wrap.style.paddingRight = '';
        wrap.style.overflow = '';
        document.documentElement.style.overflowY = '';
      });
    },

    async initOnboardingChecklist() {
      await this.loadAdminStatus();

      if (!this.isAdminUser) return;

      await this._redirectIfPendingMandatoryItems();
    },

    async _redirectIfPendingMandatoryItems() {
      try {
        await new Promise(resolve => this.$router.onReady(resolve));

        const currentRoutePath = this.$router.currentRoute?.path;

        if (currentRoutePath !== '/') return;

        const api = new OnboardingApi(this.user);
        const response = await api.getStatus();

        if (response.data.dismissed) return;

        const hasPendingMandatoryItems = response.data.items.some(item => item.isMandatory && !item.isCompleted);

        if (!hasPendingMandatoryItems) return;

        await this.$nextTick();

        if (this.$router.currentRoute?.path === '/first-steps') return;

        try {
          await this.$router.push('/first-steps');
        } catch { // eslint-disable-next-line no-empty
        }
      } catch { // eslint-disable-next-line no-empty
      }
    },
  }
};
</script>

<style lang="scss">
/* Mesmo fundo do .content-panel, não a superfície de card.
   Estava branco por baixo do painel bege: qualquer coisa que descolasse os dois
   revelava a faixa branca — os cantos arredondados no topo e, agora, o recuo
   que a Clara aplica ao redimensionar. Igualando o fundo, não há o que vazar. */
/* O APP OCUPA A VIEWPORT EXATA — a página não rola.
   Antes o `.content-panel` tinha `min-height: calc(100vh - 64px)` e o rodapé
   vinha ABAIXO dele, somando aos 64px de padding do v-main: o total sempre
   passava da viewport, então toda tela rolava. Em tela com tabela isso virava
   duas barras de rolagem — a da página e a interna da tabela.
   Agora o v-main é uma coluna de altura fixa: o painel de conteúdo é o único
   que rola, e o rodapé fica preso embaixo. */
.main-content-area {
  background-color: var(--tc-bg);
}

/* O wrap do Vuetify já é uma coluna flex, mas com `min-height: 100vh` — mínimo,
   não máximo, então ele cresce com o conteúdo. Fixando a ALTURA ele para de
   crescer e vira o limite da viewport. */
.v-application--wrap {
  height: 100vh;
}

/* Só o wrap da app raiz vale a viewport inteira. Componentes que montam um
   `v-app` aninhado (o editor TinyMCE, por exemplo) herdavam os 100vh e
   deixavam o resto da tela em branco abaixo do conteúdo. */
.v-application .v-application .v-application--wrap {
  height: auto;
}

/* O v-main pega o que sobra depois do header. Não use `height: 100vh` aqui: o
   header desta app fica no FLUXO (`position: relative`, 64px), não fixo como o
   app-bar padrão do Vuetify — 64 + 100vh dá 100vh + 64 e a página volta a rolar. */
.main-content-area.v-main {
  flex: 1 1 auto;
  min-height: 0;
  box-sizing: border-box;
}

.main-content-area.v-main > .v-main__wrap {
  display: flex;
  flex-direction: column;
  /* `min-height: 0` libera o filho flex a encolher: sem isso o painel adota a
     altura do conteúdo e volta a empurrar o rodapé para fora da tela. */
  min-height: 0;
}

.main-content-area--external.v-main {
  padding: 0 !important;
}

/* Sem raio: o conteúdo é a página, não um cartão flutuando sobre outra
   superfície. O valor aqui era `9px 12px 0 0` — assimétrico, resultado de uma
   migração que converteu só o primeiro canto para token. */
.content-panel {
  background-color: var(--tc-bg);
  border-radius: 0;
  padding: 0;
  /* Este é o ÚNICO rolador da aplicação. Telas de formulário rolam aqui; telas
     que terminam em tabela usam `.tc-screen--table` e não rolam nada aqui,
     deixando a rolagem só para a tabela. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* Container de medida para as telas: o grid md-* do Vuetify olha a VIEWPORT,
     mas a Clara recua o conteúdo sem mudá-la — só um @container enxerga o
     espaço que de fato sobrou. As telas aderem via regras `@container
     app-content` nos próprios componentes (ver FormOperation). */
  container-type: inline-size;
  container-name: app-content;
}

.content-panel--full {
  min-height: 100dvh;
  border-radius: 0;
  overflow: visible;
}

/* --tc-bg já acompanha o tema (aponta para o slot do tenant), então os dois
   seguem iguais no escuro sem precisar de override próprio. Manter valores
   diferentes aqui recriaria a faixa branca, só que em cinza. */
.theme--dark .main-content-area,
.theme--dark .content-panel {
  background-color: var(--tc-bg);
}

.versao {
  display: block;
  position: fixed;
  bottom: 0;
  right: 0;
  padding: 0px 15px 15px 5px;
}

/* Rodapé enxuto e preso ao fim da viewport: é assinatura, não conteúdo.
   Os 20px de padding somavam ~60px de altura logo abaixo da tabela e eram
   parte do que empurrava a página para além da tela. */
.footer {
  flex: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--tc-space-4);
  padding: var(--tc-space-2) var(--tc-space-5);
  font-family: var(--tc-font-mono);
  font-size: var(--tc-fs-label);
  letter-spacing: 0.04em;
  color: var(--tc-text-3);
  border-top: 1px solid var(--tc-line);
}

@media (max-width: 599px) {
  .footer {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }
}

.v-application {
  font-family: var(--tc-font-body);
  line-height: 1.5;
}

/* Remove overlay e ripple de v-card não interativos (dashboard cards, charts) */
.dashboard-card,
.card-chart {
  &.v-card::before,
  .v-ripple__container {
    display: none !important;
  }
}

/* Remove estado visual de seleção do ApexCharts nos gráficos do dashboard */
.card-chart {
  .apexcharts-selected,
  .apexcharts-active {
    opacity: 1 !important;
    filter: none !important;
  }
}
</style>
